import { useState, useCallback, useMemo, useEffect } from 'react';
import type { TaskStatus, TaskDropdownDefinition, SavedChecklist, SavedAudit } from '@techgitter/contracts';
import type { LiveChecklist, ManualTaskInput, ChecklistProgress } from '../types/taskList';
import {
  hydrateChecklist,
  snapshotChecklist,
  buildSavedAudit,
  buildAuditEntry,
  computeChecklistProgress,
  type AuditAction,
  type AuditContext,
} from '../utils/checklistEngine';
import { patchJobChecklist } from '../services/jobChecklistService';

interface UseWorkItemChecklistOptions {
  jobGuid: string;
  savedChecklist?: SavedChecklist;
  savedAudit?: SavedAudit;
  userName: string;
  /** Reusable dropdown option sets, used to resolve dropdown-task options. */
  taskDropdowns?: TaskDropdownDefinition[];
  onSaved?: (checklist: SavedChecklist, audit: SavedAudit) => void;
}

export function useWorkItemChecklist({
  jobGuid,
  savedChecklist,
  savedAudit,
  userName,
  taskDropdowns,
  onSaved,
}: UseWorkItemChecklistOptions) {
  const [checklist, setChecklist] = useState<LiveChecklist | null>(() =>
    savedChecklist ? hydrateChecklist(savedChecklist, taskDropdowns ?? []) : null,
  );
  const [auditEntries, setAuditEntries] = useState<string[]>(
    () => savedAudit?.entries ?? [],
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const progress: ChecklistProgress = useMemo(
    () =>
      checklist
        ? computeChecklistProgress(checklist)
        : { total: 0, closed: 0, new: 0, hasBlockingTasks: false },
    [checklist],
  );

  // Re-resolve dropdown-task options once the catalog loads (async), so a
  // hydrated checklist shows its options even if it was built before the
  // catalog arrived. Only `options` is touched.
  const dropdowns = useMemo(() => taskDropdowns ?? [], [taskDropdowns]);
  useEffect(() => {
    const dropdownMap = new Map(dropdowns.map((d) => [d.dropdownId, d.options]));
    setChecklist((prev) => {
      if (!prev) return prev;
      let changed = false;
      const sections = prev.sections.map((section) => {
        let tasksChanged = false;
        const tasks = section.tasks.map((task) => {
          if (!task.dropdownId) return task;
          const options = dropdownMap.get(task.dropdownId) ?? [];
          const same =
            task.options != null &&
            task.options.length === options.length &&
            task.options.every((o, i) => o === options[i]);
          if (same) return task;
          tasksChanged = true;
          return { ...task, options };
        });
        if (!tasksChanged) return section;
        changed = true;
        return { ...section, tasks };
      });
      return changed ? { sections } : prev;
    });
  }, [dropdowns]); // eslint-disable-line react-hooks/exhaustive-deps

  const addAudit = useCallback(
    (action: AuditAction, context: AuditContext) => {
      const entry = buildAuditEntry(action, context, userName);
      setAuditEntries(prev => [...prev, entry]);
    },
    [userName],
  );

  const onSetStatus = useCallback(
    (sectionId: string, taskId: string, status: TaskStatus) => {
      setChecklist(prev => {
        if (!prev) return prev;
        const sections = prev.sections.map(s => {
          if (s.sectionId !== sectionId) return s;
          return {
            ...s,
            tasks: s.tasks.map(t => {
              if (t.taskId !== taskId) return t;
              addAudit('status-change', { taskTitle: t.title, from: t.status, to: status });
              return { ...t, status };
            }),
          };
        });
        return { sections };
      });
      setIsDirty(true);
    },
    [addAudit],
  );

  const onSetNote = useCallback((sectionId: string, taskId: string, note: string) => {
    setChecklist(prev => {
      if (!prev) return prev;
      const sections = prev.sections.map(s => {
        if (s.sectionId !== sectionId) return s;
        return {
          ...s,
          tasks: s.tasks.map(t => (t.taskId === taskId ? { ...t, note } : t)),
        };
      });
      return { sections };
    });
    setIsDirty(true);
  }, []);

  const onSetOption = useCallback(
    (sectionId: string, taskId: string, option: string) => {
      const selectedOption = option || undefined;
      const nextStatus: TaskStatus = selectedOption ? 'completed' : 'new';
      setChecklist(prev => {
        if (!prev) return prev;
        const sections = prev.sections.map(s => {
          if (s.sectionId !== sectionId) return s;
          return {
            ...s,
            tasks: s.tasks.map(t => {
              if (t.taskId !== taskId) return t;
              if (t.status !== nextStatus) {
                addAudit('status-change', { taskTitle: t.title, from: t.status, to: nextStatus });
              }
              return { ...t, selectedOption, status: nextStatus };
            }),
          };
        });
        return { sections };
      });
      setIsDirty(true);
    },
    [addAudit],
  );

  const onRemoveTask = useCallback(
    (sectionId: string, taskId: string) => {
      setChecklist(prev => {
        if (!prev) return prev;
        const sections = prev.sections.map(s => {
          if (s.sectionId !== sectionId) return s;
          return {
            ...s,
            tasks: s.tasks.map(t => {
              if (t.taskId !== taskId) return t;
              addAudit('task-remove', { taskTitle: t.title });
              return { ...t, isRemoved: true };
            }),
          };
        });
        return { sections };
      });
      setIsDirty(true);
    },
    [addAudit],
  );

  const onRestoreTask = useCallback(
    (sectionId: string, taskId: string) => {
      setChecklist(prev => {
        if (!prev) return prev;
        const sections = prev.sections.map(s => {
          if (s.sectionId !== sectionId) return s;
          return {
            ...s,
            tasks: s.tasks.map(t => {
              if (t.taskId !== taskId) return t;
              addAudit('task-restore', { taskTitle: t.title });
              return { ...t, isRemoved: false };
            }),
          };
        });
        return { sections };
      });
      setIsDirty(true);
    },
    [addAudit],
  );

  const onRemoveSection = useCallback(
    (sectionId: string) => {
      setChecklist(prev => {
        if (!prev) return prev;
        const sections = prev.sections.map(s => {
          if (s.sectionId !== sectionId) return s;
          addAudit('section-remove', { sectionLabel: s.label });
          return { ...s, isRemoved: true };
        });
        return { sections };
      });
      setIsDirty(true);
    },
    [addAudit],
  );

  const onRestoreSection = useCallback(
    (sectionId: string) => {
      setChecklist(prev => {
        if (!prev) return prev;
        const sections = prev.sections.map(s => {
          if (s.sectionId !== sectionId) return s;
          addAudit('section-restore', { sectionLabel: s.label });
          return { ...s, isRemoved: false };
        });
        return { sections };
      });
      setIsDirty(true);
    },
    [addAudit],
  );

  const onAddManualTask = useCallback(
    (input: ManualTaskInput) => {
      setChecklist(prev => {
        if (!prev) return prev;
        const sections = prev.sections.map(s => {
          if (s.sectionId !== input.sectionId) return s;
          addAudit('task-add', { taskTitle: input.title, sectionLabel: s.label });
          return {
            ...s,
            tasks: [
              ...s.tasks,
              {
                taskId: `manual-${Date.now()}`,
                title: input.title,
                description: input.description,
                status: 'new' as TaskStatus,
                note: '',
                noteRequired: input.noteRequired,
                isRemoved: false,
                isVisible: true,
                source: 'manual' as const,
              },
            ],
          };
        });
        return { sections };
      });
      setIsDirty(true);
    },
    [addAudit],
  );

  const initChecklist = useCallback((newChecklist: LiveChecklist) => {
    setChecklist(newChecklist);
    setIsDirty(true);
  }, []);

  /**
   * Append additional sections to an existing checklist (or start a
   * new one if none exists yet). Used when attaching a second or
   * subsequent task list to the same work item.
   */
  const appendChecklist = useCallback((extra: LiveChecklist) => {
    setChecklist(prev => {
      if (!prev || prev.sections.length === 0) return extra;
      return { sections: [...prev.sections, ...extra.sections] };
    });
    setIsDirty(true);
  }, []);

  const onSave = useCallback(async () => {
    if (!checklist) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const snapshot = snapshotChecklist(checklist, new Date().toISOString());
      const audit = buildSavedAudit(auditEntries);
      await patchJobChecklist(jobGuid, snapshot, audit);
      setIsDirty(false);
      onSaved?.(snapshot, audit);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save checklist');
    } finally {
      setIsSaving(false);
    }
  }, [checklist, auditEntries, jobGuid, onSaved]);

  return {
    checklist,
    progress,
    isDirty,
    isSaving,
    saveError,
    initChecklist,
    appendChecklist,
    onSetStatus,
    onSetNote,
    onSetOption,
    onRemoveTask,
    onRestoreTask,
    onRemoveSection,
    onRestoreSection,
    onAddManualTask,
    onSave,
  };
}
